import { useEffect, useState, useRef, useCallback } from "react";
import { readEmailContext } from "../office/context";
import { useConversation } from "../hooks/useConversation";
import { ContextHeader } from "./ContextHeader";
import { MessageBubble } from "./MessageBubble";

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 14V3M8 3L3 8M8 3L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const INTENT_CHIPS = [
  { label: "Freundlich", value: "freundlich antworten" },
  { label: "Kurz & knapp", value: "kurz und knapp antworten" },
  { label: "Professionell", value: "professionell antworten" },
  { label: "Termin vorschlagen", value: "Termin vorschlagen" },
  { label: "Absagen", value: "freundlich absagen" },
  { label: "Rückfrage stellen", value: "Rückfrage stellen" },
];

export function TaskPane() {
  const {
    state,
    setEmailContext,
    generateReply,
    refineReply,
    insertDraft,
    clearError,
  } = useConversation();

  const [instruction, setInstruction] = useState("");
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function load() {
      try {
        const ctx = await readEmailContext();
        setEmailContext(ctx);
      } catch (err) {
        setContextError(
          err instanceof Error ? err.message : "E-Mail-Kontext konnte nicht gelesen werden"
        );
      } finally {
        setContextLoading(false);
      }
    }
    load();
  }, [setEmailContext]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [state.messages]);

  const handleSend = (text?: string) => {
    const value = (text ?? instruction).trim();
    if (!value) return;
    setInstruction("");
    if (state.conversationId) {
      refineReply(value);
    } else {
      generateReply(value);
    }
  };

  const handleChipClick = (value: string) => {
    const combined = instruction.trim()
      ? `${instruction.trim()}, ${value}`
      : value;
    handleSend(combined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 150) + "px";
  }, []);

  const hasMessages = state.messages.length > 0;
  const canSend = !state.loading && !state.streaming && !!state.emailContext;
  const canInsertDraft = state.draftReady && !state.loading;
  const showSend = canSend && instruction.trim().length > 0;

  return (
    <div className="taskpane">
      <div className="taskpane__header">
        <div className="taskpane__brand">
          <div className="taskpane__logo">
            <img src="/assets/icon-64.png" alt="Lio" />
          </div>
          <span className="taskpane__title">Lio</span>
        </div>
        <span className={`taskpane__status ${state.streaming ? 'taskpane__status--active' : ''}`}>
          {state.streaming ? '● Generiert …' : ''}
        </span>
      </div>

      <div className="taskpane__body">
        {contextLoading && (
          <div className="taskpane__loading">
            <div className="taskpane__spinner" />
            E-Mail wird gelesen …
          </div>
        )}

        {contextError && (
          <div className="taskpane__error-fullpage">{contextError}</div>
        )}

        {!contextLoading && !contextError && (
          <>
            {state.emailContext && <ContextHeader context={state.emailContext} />}

            {state.error && (
              <div className="taskpane__error-banner" onClick={clearError}>
                {state.error}
              </div>
            )}

            {!hasMessages && (
              <div className="taskpane__empty">
                <p className="taskpane__empty-heading">Wie soll ich antworten?</p>

                <div className="taskpane__chips">
                  {INTENT_CHIPS.map((chip) => (
                    <button
                      key={chip.value}
                      className="chip"
                      onClick={() => handleChipClick(chip.value)}
                      disabled={!canSend}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>

                <div className="taskpane__empty-input-group">
                  <textarea
                    ref={textareaRef}
                    className="taskpane__input taskpane__input--empty"
                    value={instruction}
                    onChange={(e) => { setInstruction(e.target.value); autoResize(); }}
                    onKeyDown={handleKeyDown}
                    placeholder={'z.\u00a0B. "freundlich absagen" oder "Termin nächste Woche vorschlagen"'}
                    rows={2}
                    disabled={!canSend}
                  />
                  <button
                    className="btn btn--cta"
                    onClick={() => handleSend()}
                    disabled={!showSend}
                  >
                    <span>Vorschlag erstellen</span>
                    <ArrowIcon />
                  </button>
                </div>
              </div>
            )}

            {hasMessages && (
              <div className="taskpane__messages">
                {state.messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}
                {state.sources.length > 0 && !state.streaming && (
                  <div className="taskpane__sources">
                    <span className="taskpane__sources-label">Quellen:</span>
                    {state.sources.map((s, i) => (
                      <span key={i} className="taskpane__sources-item">{s.title || s.file}</span>
                    ))}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}

            {hasMessages && (
              <div className="taskpane__controls">
                <div className="taskpane__input-row">
                  <textarea
                    ref={!hasMessages ? undefined : textareaRef}
                    className="taskpane__input"
                    value={instruction}
                    onChange={(e) => { setInstruction(e.target.value); autoResize(); }}
                    onKeyDown={handleKeyDown}
                    placeholder="Nachricht an Lio …"
                    rows={2}
                    disabled={!canSend}
                  />
                  {showSend && (
                    <button
                      className="btn btn--send"
                      onClick={() => handleSend()}
                      title="Absenden"
                    >
                      <SendIcon />
                    </button>
                  )}
                </div>

                {canInsertDraft && (
                  <div className="taskpane__draft-actions">
                    <button
                      className="btn btn--primary"
                      onClick={insertDraft}
                      disabled={!canInsertDraft}
                    >
                      In Antwort übernehmen
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
